import { useRef } from 'react';
import { motion, useMotionValue, useTransform, useSpring } from 'framer-motion';
import { Sparkles, Send, Target, Zap, BarChart3 } from 'lucide-react';

const MSG_VARIANTS = {
  hidden: { opacity: 0, y: 12 },
  visible: (d) => ({ opacity: 1, y: 0, transition: { delay: d, duration: 0.4, ease: 'easeOut' } }),
};

const FLOAT = {
  animate: (phase = 0) => ({
    y: [0, -8, 0],
    transition: { duration: 3 + phase, repeat: Infinity, ease: 'easeInOut', delay: phase },
  }),
};

export default function ChatMockup3D() {
  const wrapRef = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useTransform(useSpring(my, { stiffness: 80, damping: 20 }), [-0.5, 0.5], [4, -4]);
  const rotateY = useTransform(useSpring(mx, { stiffness: 80, damping: 20 }), [-0.5, 0.5], [-8, 8]);

  const onMove = (e) => {
    const rect = wrapRef.current?.getBoundingClientRect();
    if (!rect) return;
    mx.set((e.clientX - rect.left) / rect.width - 0.5);
    my.set((e.clientY - rect.top) / rect.height - 0.5);
  };
  const onLeave = () => { mx.set(0); my.set(0); };

  return (
    <div
      ref={wrapRef}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      className="relative flex items-center justify-center"
      style={{ perspective: '1200px', minHeight: 480 }}
    >
      {/* Phone frame */}
      <motion.div
        style={{ rotateX, rotateY, transformStyle: 'preserve-3d' }}
        initial={{ rotateY: 12, opacity: 0, y: 30 }}
        animate={{ rotateY: 8, opacity: 1, y: 0 }}
        transition={{ duration: 1, ease: 'easeOut', delay: 0.4 }}
        className="relative"
        sx={{ width: 340 }}
      >
        <div style={{
          width: 320,
          background: 'var(--surface)',
          border: '0.5px solid rgba(255,255,255,0.1)',
          borderRadius: 24,
          padding: 14,
          boxShadow: '0 0 0 1px rgba(255,255,255,0.05), 0 40px 80px rgba(0,0,0,0.7), 0 0 60px rgba(255,107,0,0.08)',
          overflow: 'hidden',
        }}>
          {/* Chat header */}
          <div style={{ background: 'rgba(255,107,0,0.9)', borderRadius: 12, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <div style={{ width: 32, height: 32, background: 'rgba(0,0,0,0.25)', borderRadius: 999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Sparkles size={14} color="white" />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ color: 'white', fontWeight: 700, fontSize: 13, fontFamily: 'Cabinet Grotesk, sans-serif' }}>Chanakya</div>
              <div style={{ color: 'rgba(255,255,255,0.75)', fontSize: 10 }}>Your AI · Always on · Never judges you</div>
            </div>
            <div style={{ width: 8, height: 8, background: '#4ade80', borderRadius: 999 }} />
          </div>

          {/* Messages */}
          <div style={{ minHeight: 260, display: 'flex', flexDirection: 'column', gap: 10, overflow: 'hidden' }}>
            <motion.div custom={0.8} variants={MSG_VARIANTS} initial="hidden" animate="visible" style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <div style={{ background: '#FF6B00', color: 'white', borderRadius: '14px 14px 4px 14px', padding: '10px 14px', fontSize: 12, maxWidth: 200, fontFamily: 'DM Sans, sans-serif' }}>
                Paid SBI home loan ₹28,500 today
              </div>
            </motion.div>

            <motion.div custom={1.1} variants={MSG_VARIANTS} initial="hidden" animate="visible" style={{ display: 'flex', gap: 8 }}>
              <div style={{ width: 26, height: 26, background: '#fff3e0', borderRadius: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 4 }}>
                <Sparkles size={11} color="#FF6B00" />
              </div>
              <div style={{ background: 'var(--surface-2)', border: '0.5px solid rgba(255,255,255,0.08)', borderRadius: '14px 14px 14px 4px', padding: '10px 14px', maxWidth: 220 }}>
                <div style={{ color: '#4ade80', fontWeight: 700, fontSize: 12, fontFamily: 'DM Sans, sans-serif' }}>✅ EMI logged!</div>
                <div style={{ color: 'var(--muted)', fontSize: 11, marginTop: 3, fontFamily: 'DM Sans, sans-serif', lineHeight: 1.5 }}>Balance: ₹18.4L · 58 months left · You're 38% done 🏠</div>
              </div>
            </motion.div>

            <motion.div custom={1.4} variants={MSG_VARIANTS} initial="hidden" animate="visible" style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <div style={{ background: '#FF6B00', color: 'white', borderRadius: '14px 14px 4px 14px', padding: '10px 14px', fontSize: 12, fontFamily: 'DM Sans, sans-serif' }}>
                How much Zomato this month?
              </div>
            </motion.div>

            <motion.div custom={1.7} variants={MSG_VARIANTS} initial="hidden" animate="visible" style={{ display: 'flex', gap: 8 }}>
              <div style={{ width: 26, height: 26, background: '#fff3e0', borderRadius: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 4 }}>
                <Sparkles size={11} color="#FF6B00" />
              </div>
              <div style={{ background: 'var(--surface-2)', border: '0.5px solid rgba(255,255,255,0.08)', borderRadius: '14px 14px 14px 4px', padding: '12px 14px' }}>
                <div style={{ color: '#FF6B00', fontWeight: 800, fontSize: 24, fontFamily: 'Cabinet Grotesk, sans-serif', lineHeight: 1 }}>₹4,820</div>
                <div style={{ color: 'var(--muted)', fontSize: 11, marginTop: 2, fontFamily: 'DM Sans, sans-serif' }}>on food delivery 😅 That's 3× your grocery spend.</div>
              </div>
            </motion.div>
          </div>

          {/* Input bar */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12, padding: '10px 0 4px', borderTop: '0.5px solid rgba(255,255,255,0.08)' }}>
            <div style={{ flex: 1, background: 'rgba(255,255,255,0.05)', border: '0.5px solid rgba(255,255,255,0.1)', borderRadius: 12, padding: '8px 12px', fontSize: 12, color: 'var(--muted)', fontFamily: 'DM Sans, sans-serif' }}>
              Type any transaction…
            </div>
            <div style={{ width: 34, height: 34, background: '#FF6B00', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Send size={13} color="white" />
            </div>
          </div>
        </div>

        {/* Floating card A — top right */}
        <motion.div
          custom={0}
          variants={MSG_VARIANTS}
          initial="hidden"
          animate="visible"
          className="float-card" style={{ position: 'absolute', top: -20, right: -70, zIndex: 10 }}
        >
          <motion.div
            animate={{ y: [0, -7, 0] }}
            transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
            style={{ background: 'var(--surface)', border: '0.5px solid rgba(255,255,255,0.1)', borderRadius: 12, padding: '10px 14px', width: 160, display: 'flex', gap: 8, alignItems: 'center', boxShadow: '0 8px 24px rgba(0,0,0,0.4)' }}
          >
            <div style={{ background: '#fff3e0', borderRadius: 8, padding: 6 }}><Zap size={13} color="#FF6B00" /></div>
            <div>
              <div style={{ color: 'var(--muted)', fontSize: 10, fontFamily: 'DM Sans, sans-serif' }}>EMI due in 3 days</div>
              <div style={{ color: '#FF6B00', fontWeight: 700, fontSize: 12, fontFamily: 'Cabinet Grotesk, sans-serif' }}>HDFC Car Loan</div>
            </div>
          </motion.div>
        </motion.div>

        {/* Floating card B — bottom left */}
        <motion.div
          custom={0.3}
          variants={MSG_VARIANTS}
          initial="hidden"
          animate="visible"
          className="float-card" style={{ position: 'absolute', bottom: -24, left: -80, zIndex: 10 }}
        >
          <motion.div
            animate={{ y: [0, 7, 0] }}
            transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut', delay: 0.5 }}
            style={{ background: 'var(--surface)', border: '0.5px solid rgba(255,255,255,0.1)', borderRadius: 12, padding: '10px 14px', width: 160, display: 'flex', gap: 8, alignItems: 'center', boxShadow: '0 8px 24px rgba(0,0,0,0.4)' }}
          >
            <div style={{ background: '#e6faf0', borderRadius: 8, padding: 6 }}><BarChart3 size={13} color="#22c55e" /></div>
            <div>
              <div style={{ color: 'var(--muted)', fontSize: 10, fontFamily: 'DM Sans, sans-serif' }}>Health Score</div>
              <div style={{ color: '#22c55e', fontWeight: 700, fontSize: 14, fontFamily: 'Cabinet Grotesk, sans-serif' }}>84 · Excellent</div>
            </div>
          </motion.div>
        </motion.div>

        {/* Floating card C — goal */}
        <motion.div
          custom={0.6}
          variants={MSG_VARIANTS}
          initial="hidden"
          animate="visible"
          className="float-card" style={{ position: 'absolute', top: '45%', right: -80, zIndex: 10 }}
        >
          <motion.div
            animate={{ y: [0, -5, 0] }}
            transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
            style={{ background: 'var(--surface)', border: '0.5px solid rgba(255,255,255,0.1)', borderRadius: 12, padding: '10px 14px', width: 155, display: 'flex', gap: 8, alignItems: 'center', boxShadow: '0 8px 24px rgba(0,0,0,0.4)' }}
          >
            <div style={{ background: '#e6faf0', borderRadius: 8, padding: 6 }}><Target size={13} color="#22c55e" /></div>
            <div>
              <div style={{ color: '#22c55e', fontWeight: 600, fontSize: 10, fontFamily: 'DM Sans, sans-serif' }}>🎯 Goal reached!</div>
              <div style={{ color: 'white', fontSize: 11, fontFamily: 'DM Sans, sans-serif' }}>Europe trip 100% 🎉</div>
            </div>
          </motion.div>
        </motion.div>
      </motion.div>
    </div>
  );
}
